import * as React from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import {
  useApplyExchangeRatesMutation,
  useGetExchangeRatesQuery,
  useGetRateDiagnosticsQuery,
  useLazyGetExchangeRatePreviewQuery,
} from "@/api/exchange-rates";
import { useGetSpendSettingsQuery } from "@/api/spend";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useSuppliersAccess } from "@/hooks/use-suppliers-access";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { RatePreview, RateReceipt, RateRow } from "@/types/exchange-rates";
import { RateCoverage } from "./components/rate-coverage";
import { RateTable } from "./components/rate-table";
import { RateImportForm } from "./components/rate-import-form";
import { rateErrorMessage } from "./rate-errors";
import { useSpendQueryState } from "./use-spend-query-state";

export const ExchangeRatesPage = () => {
  const user = useAuthenticatedUser();
  const { isSpendCentreAdmin } = useSuppliersAccess();
  if (user.isLoading) return <p>Loading organization…</p>;
  if (!isSpendCentreAdmin) return <Navigate replace to="/spend-centre" />;
  return <ExchangeRatesWorkspace organizationName={user.organization.name} />;
};

interface Props {
  organizationName: string;
}

const ExchangeRatesWorkspace = ({ organizationName }: Props) => {
  const [params] = useSearchParams();
  const { data: settings } = useGetSpendSettingsQuery();
  const state = useSpendQueryState(settings?.default_period_type);
  const context = {
    period_type: state.periodType,
    year: state.year,
    owner_user_id: state.ownerUserId || undefined,
    category: state.category || undefined,
    search: state.search || undefined,
    sort: state.sort,
    direction: state.direction,
    offset: 0,
    limit: 25,
  };
  const [source, setSource] = React.useState("");
  const [currency, setCurrency] = React.useState("");
  const [history, setHistory] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [historyOffset, setHistoryOffset] = React.useState(0);
  const [editing, setEditing] = React.useState(false);
  const [reference, setReference] = React.useState<RateRow>();
  const [preview, setPreview] = React.useState<RatePreview>();
  const [receipt, setReceipt] = React.useState<RateReceipt>();
  const previewHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const receiptHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const previewId = preview?.preview_id;
  const receiptId = receipt?.preview_id;
  React.useEffect(() => {
    if (previewId) previewHeadingRef.current?.focus();
  }, [previewId]);
  React.useEffect(() => {
    if (receiptId) receiptHeadingRef.current?.focus();
  }, [receiptId]);

  const [applyError, setApplyError] = React.useState<unknown>();
  const errorRef = React.useRef<HTMLParagraphElement>(null);
  React.useEffect(() => {
    if (applyError) errorRef.current?.focus();
  }, [applyError]);
  const [apply, applyState] = useApplyExchangeRatesMutation();
  const [retrievePreview, retrievalState] = useLazyGetExchangeRatePreviewQuery();
  const inventory = useGetExchangeRatesQuery(
    {
      source: source || undefined,
      currency: currency || undefined,
      history,
      offset,
      limit: 25,
      history_offset: historyOffset,
      history_limit: 25,
    },
    { skip: !settings },
  );
  const diagnostics = useGetRateDiagnosticsQuery(context, { skip: !settings });
  const { formatDateTime, formatDate } = useDateFormatter();
  const selectedSource = inventory.data?.selected_fx_source ?? settings?.selected_fx_source ?? "";
  const reuse = (row: RateRow) => {
    setReference(row);
    setPreview(undefined);
    setReceipt(undefined);
    setEditing(true);
  };
  const applyPreview = async () => {
    if (!preview) return;
    setApplyError(undefined);
    try {
      setReceipt(await apply(preview.preview_id).unwrap());
      setPreview(undefined);
    } catch (error) {
      setApplyError(error);
    }
  };
  const checkPreview = async () => {
    if (!preview) return;
    try {
      const reviewed = await retrievePreview(preview.preview_id).unwrap();
      if (reviewed.result) {
        setReceipt(reviewed.result);
        setPreview(undefined);
      } else setPreview(reviewed);
      setApplyError(undefined);
    } catch (error) {
      setApplyError(error);
    }
  };
  return (
    <main className="mx-auto max-w-7xl space-y-5" aria-label="Exchange rates workspace">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to={`/spend-centre?${params.toString()}`} className="text-sm underline">
            Back to Spend Centre
          </Link>
          <h1 className="mt-3 text-3xl font-bold">Exchange rates</h1>
          <p>
            Organization: <strong>{organizationName}</strong>
          </p>
          <p className="text-sm text-gray-500">
            Reporting context: {state.periodType} {state.year}
            {state.ownerUserId ? " · Owner filter active" : " · All owners"}
            {state.category ? ` · ${state.category}` : ""}
            {state.search ? ` · Search: ${state.search}` : ""}
          </p>
        </div>
        <Button
          onClick={() => {
            setReference(undefined);
            setPreview(undefined);
            setReceipt(undefined);
            setEditing(true);
          }}
          disabled={editing || !!preview || applyState.isLoading}
        >
          Import workbook
        </Button>
      </header>
      {diagnostics.data ? <RateCoverage diagnostics={diagnostics.data} /> : null}
      {diagnostics.isLoading ? <p role="status">Loading valuation context…</p> : null}
      {diagnostics.isError ? (
        <div role="alert">
          Valuation context could not be loaded.{" "}
          <Button variant="outline" onClick={() => diagnostics.refetch()}>
            Retry diagnostics
          </Button>
        </div>
      ) : null}
      {editing || preview ? (
        <div hidden={!editing}>
          <RateImportForm
            key={reference?.id ?? "workbook"}
            source={source || selectedSource}
            sources={inventory.data?.sources ?? []}
            context={context}
            reference={reference}
            onPreview={value => {
              setPreview(value);
              setEditing(false);
              setApplyError(undefined);
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : null}
      {preview ? (
        <section className="space-y-4 rounded-lg border bg-white p-5" aria-label="Rate preview">
          <h2 ref={previewHeadingRef} tabIndex={-1} className="text-xl font-semibold">
            Review rates before import
          </h2>
          <p>
            {preview.source} · {preview.period_label} · Expires {formatDateTime(preview.expires_at)}
          </p>
          {preview.source !== selectedSource ? (
            <p className="text-amber-800">
              This source is not selected for Spend Centre. Importing does not change the selected FX source.
            </p>
          ) : null}
          <RateTable rows={preview.rows} />
          <p>
            Proposed changes: {preview.rows.filter(row => row.classification === "new").length} new,{" "}
            {preview.rows.filter(row => row.classification === "unchanged").length} unchanged,{" "}
            {preview.rows.filter(row => row.classification === "superseded").length} superseded.
          </p>
          <p>
            {preview.skipped_count ?? preview.skipped_codes.length} skipped rows; skipped codes:{" "}
            {preview.skipped_codes.join(", ") || "None"}
          </p>
          {preview.blockers.map(blocker => (
            <p role="alert" key={blocker} className="text-red-700">
              {blocker === "missing_period_must_be_new"
                ? "These dates already have an approved rate. Choose a missing period, or import a workbook to correct an existing interval."
                : blocker}
            </p>
          ))}
          <RateCoverage diagnostics={preview.diagnostics} />
          {applyError ? (
            <p ref={errorRef} tabIndex={-1} role="alert" className="text-red-700">
              {rateErrorMessage(applyError)}{" "}
              <Button variant="outline" onClick={checkPreview} disabled={retrievalState.isFetching}>
                Check preview status
              </Button>
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button onClick={applyPreview} disabled={applyState.isLoading || !!preview.blockers.length}>
              {applyState.isLoading ? "Importing…" : "Import approved rates"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPreview(undefined);
                setEditing(true);
              }}
              disabled={applyState.isLoading}
            >
              Edit configuration
            </Button>
            <Button variant="outline" onClick={() => setPreview(undefined)} disabled={applyState.isLoading}>
              Cancel preview
            </Button>
          </div>
        </section>
      ) : null}
      {receipt ? (
        <section className="space-y-3 rounded-lg border bg-white p-5" aria-label="Import result" role="status">
          <h2 ref={receiptHeadingRef} tabIndex={-1} className="text-xl font-semibold">
            Import complete
          </h2>
          <p>
            {receipt.accepted_count} accepted, {receipt.unchanged_count} unchanged, {receipt.superseded_count}{" "}
            superseded · {formatDateTime(receipt.completed_at)}
          </p>
          <p>
            {receipt.source} · {receipt.period_label} · {formatDate(receipt.valid_from)} through{" "}
            {formatDate(receipt.valid_through)} (inclusive)
          </p>
          {receipt.reason ? <p>Recorded reason: {receipt.reason}</p> : null}
          <p>
            {receipt.skipped_count ?? receipt.skipped_codes.length} skipped rows; skipped codes:{" "}
            {receipt.skipped_codes.join(", ") || "None"}
          </p>
          {receipt.source !== selectedSource ? (
            <p>This import used an unselected source. Spend Centre continues to use {selectedSource}.</p>
          ) : null}
          <p>
            Spend estimates have been refreshed. Stored invoice Actuals were not reprocessed; spend coverage and
            contract metadata remain unchanged.
          </p>
          <RateCoverage diagnostics={receipt.diagnostics} />
        </section>
      ) : null}
      <section className="rounded-lg border bg-white p-5" aria-label="Exchange rate inventory">
        <h2 className="mb-4 text-xl font-semibold">Approved rates</h2>
        <div className="mb-4 flex flex-wrap gap-4">
          <label>
            Source
            <select
              className="ml-2 rounded border p-2"
              value={source}
              onChange={event => {
                setSource(event.target.value);
                setOffset(0);
                setHistoryOffset(0);
              }}
            >
              <option value="">Selected source ({selectedSource})</option>
              {inventory.data?.sources.map(value => (
                <option key={value} value={value}>
                  {value}
                  {value === selectedSource ? " (selected)" : " (unselected)"}
                </option>
              ))}
            </select>
          </label>
          <label>
            Currency
            <Input
              value={currency}
              maxLength={3}
              onChange={event => {
                setCurrency(event.target.value.toUpperCase());
                setOffset(0);
              }}
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={history}
              onChange={event => {
                setHistory(event.target.checked);
                setOffset(0);
              }}
            />
            Include historical versions
          </label>
        </div>
        {inventory.isLoading ? <p role="status">Loading exchange rates…</p> : null}
        {inventory.isError ? (
          <p role="alert">
            Rates could not be loaded.{" "}
            <Button variant="outline" onClick={() => inventory.refetch()}>
              Retry rates
            </Button>
          </p>
        ) : null}
        {inventory.data ? (
          <>
            <RateTable
              rows={inventory.data.items}
              onReuse={editing || preview || applyState.isLoading ? undefined : reuse}
            />
            <div className="mt-4 flex items-center gap-4">
              <Button
                variant="outline"
                disabled={!offset || inventory.isFetching}
                onClick={() => setOffset(Math.max(0, offset - 25))}
              >
                Previous rates
              </Button>
              <p>
                {inventory.data.total} rates · Page {Math.floor(offset / 25) + 1}
              </p>
              <Button
                variant="outline"
                disabled={offset + 25 >= inventory.data.total || inventory.isFetching}
                onClick={() => setOffset(offset + 25)}
              >
                Next rates
              </Button>
            </div>
          </>
        ) : null}
      </section>
      <section className="rounded-lg border bg-white p-5" aria-label="Import history">
        <h2 className="text-xl font-semibold">Import history</h2>
        {inventory.data?.batches?.length ? (
          <ul className="mt-3 space-y-3" aria-label="All FX import batches">
            {inventory.data.batches.map(batch => (
              <li key={batch.batch_id}>
                {batch.completed_at ? formatDateTime(batch.completed_at) : "Completion time unavailable"} ·{" "}
                {batch.source} · {batch.accepted_count} accepted
                {batch.external_batch_id ? ` · ${batch.external_batch_id}` : ""}
                <details>
                  <summary>Import audit details</summary>
                  <p>Batch: {batch.batch_id}</p>
                  <p>Actor: {batch.actor_user_id ?? "Unavailable"}</p>
                  <p>Input digest: {batch.input_digest}</p>
                </details>
              </li>
            ))}
          </ul>
        ) : null}
        <h3 className="mt-4 font-semibold">Reviewed import receipts</h3>

        {inventory.data?.history.length ? (
          <ul className="mt-3 space-y-3">
            {inventory.data.history.map(item => (
              <li key={item.preview_id}>
                {formatDateTime(item.completed_at)} · {item.source} · {item.period_label} · {item.accepted_count}{" "}
                accepted / {item.unchanged_count} unchanged / {item.superseded_count} superseded
                {item.reason ? <p>Reason: {item.reason}</p> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3">No imports to display.</p>
        )}
        {inventory.data ? (
          <div className="mt-4 flex items-center gap-4" role="group" aria-label="Import history pagination">
            <Button
              variant="outline"
              disabled={!historyOffset || inventory.isFetching}
              onClick={() => setHistoryOffset(Math.max(0, historyOffset - 25))}
            >
              Previous imports
            </Button>
            <p>Page {Math.floor(historyOffset / 25) + 1}</p>
            <Button
              variant="outline"
              disabled={historyOffset + 25 >= inventory.data.history_total || inventory.isFetching}
              onClick={() => setHistoryOffset(historyOffset + 25)}
            >
              Next imports
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  );
};
